import {useEffect, useState, type FocusEvent, type KeyboardEvent} from "react";
import {
    useFormItems,
    type AlternativesItem,
    type Company,
    type ConditionItem,
    type ListItem,
    type OptionalItem,
    type VariableItem,
} from "@dibkunnskap/dokumentgenerator";
import {DocumentGeneratorInput} from "./DocumentGeneratorInput";
import {ConditionalsForm} from "./ConditionalsForm";
import {FormLabel} from "./FormLabel";
import {DropdownMenu} from "./DropdownMenu";
import {DropdownMenuItem} from "./DropdownMenuItem";
import {OptionsForm} from "./OptionsForm";
import {XAlternativesForm} from "./XAlternativesForm";
import {XListForm} from "./XListForm";
import {OptionalForm} from "./OptionalForm";
import {UndoButton} from "./UndoButton";

interface DraftProps {
    isDraft: boolean;
    /** Draft gating: focusing a field prompts for a new filling instead. */
    onPromptNewFilling: (e?: FocusEvent) => void;
}

const suggestionKey = (s: string | Company) =>
    typeof s === "string" ? s : s.orgnr;

/**
 * A variable input with its suggestion list (variable suggestions or
 * company lookup). Keyboard: arrows move, Enter/Tab pick; click picks.
 */
const VariableField = ({
    item,
    isDraft,
    onPromptNewFilling,
}: {item: VariableItem} & DraftProps) => {
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(-1);
    const items = item.suggestions.items as (string | Company)[];
    const isOpen = open && items.length > 0;

    useEffect(() => {
        setHighlighted(-1);
    }, [items]);

    const pick = (s: string | Company) => {
        if (typeof s === "string") {
            item.select(s);
        } else {
            item.selectCompany(s);
        }
        setOpen(false);
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen) return;
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setHighlighted((i) => (i + 1) % items.length);
                break;
            case "ArrowUp":
                e.preventDefault();
                setHighlighted((i) => (i <= 0 ? items.length - 1 : i - 1));
                break;
            case "Enter":
            case "Tab":
                if (highlighted >= 0 && items[highlighted] !== undefined) {
                    if (e.key === "Enter") e.preventDefault();
                    pick(items[highlighted]);
                }
                break;
            case "Escape":
                setOpen(false);
                break;
            default:
                break;
        }
    };

    // Radio-style variables get no help text: the old app never rendered the
    // editor's comment for a variable with options, only for typed input.
    if (item.options) {
        return (
            <OptionsForm
                ref={item.focus.ref}
                id={item.inputId}
                name={item.name}
                options={item.options}
                value={item.value}
                onChange={(e) => item.select(e.target.value)}
                onFocus={(e) => {
                    onPromptNewFilling(e);
                    item.focus.onFocus(e);
                }}
                onBlur={(e) => item.focus.onBlur(e)}
            />
        );
    }

    return (
        <div
            className="xvar-form"
            ref={item.focus.ref}
            onFocus={(e) => {
                item.focus.onFocus(e);
                setOpen(true);
            }}
            onBlur={(e) => {
                item.focus.onBlur(e);
                if (!e.currentTarget.contains(e.relatedTarget)) {
                    setOpen(false);
                }
            }}
        >
            <FormLabel value={item.name} htmlFor={item.inputId}>
                <div className="xvar-form-input-wrapper">
                    <DocumentGeneratorInput
                        inputProps={{
                            id: item.inputId,
                            value: item.value,
                            onChange: (e: {target: {value: string}}) => {
                                item.setValue(e.target.value);
                                setOpen(true);
                            },
                            onKeyDown,
                        }}
                        comment={item.comment ?? ""}
                        companySearchEnabled={item.companySearch}
                        setDropdownOpen={setOpen}
                        suggestionList={items as string[]}
                        searching={item.suggestions.loading}
                        isDraft={isDraft}
                        onPromptNewFilling={onPromptNewFilling}
                    />
                    {item.reset && <UndoButton onClick={item.reset} />}
                </div>
            </FormLabel>
            <DropdownMenu isOpen={isOpen}>
                {items.map((s, index) => (
                    <DropdownMenuItem
                        key={suggestionKey(s)}
                        role="option"
                        aria-selected={index === highlighted}
                        isActive={index === highlighted}
                        isSelected={suggestionKey(s) === item.value}
                        // Keep focus in the input so blur does not fire before click.
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseMove={() => setHighlighted(index)}
                        onClick={() => pick(s)}
                    >
                        {typeof s === "string" ? (
                            s
                        ) : (
                            <div>
                                <p>{s.navn}</p>
                                <span>
                                    {s.orgnr}, {s.sted}
                                </span>
                            </div>
                        )}
                    </DropdownMenuItem>
                ))}
            </DropdownMenu>
        </div>
    );
};

const OptionalField = ({
    item,
    onPromptNewFilling,
}: {item: OptionalItem} & DraftProps) => (
    <OptionalForm
        ref={item.focus.ref}
        id={item.id}
        checked={item.checked}
        hasFocus={item.focus.focused}
        keyword={item.keyword ?? ""}
        text={item.description.text}
        textSegments={item.description.segments}
        heading={item.heading}
        onChange={item.toggle}
        onFocus={(e?: FocusEvent) => {
            onPromptNewFilling(e);
            item.focus.onFocus(e);
        }}
        onBlur={(e?: FocusEvent) => item.focus.onBlur(e)}
    />
);

const AlternativesField = ({
    item,
    onPromptNewFilling,
}: {item: AlternativesItem} & DraftProps) => (
    <XAlternativesForm
        ref={item.focus.ref}
        id={`x-alternatives-form-${item.id}`}
        displayValue={item.selectedId}
        hasFocus={item.focus.focused}
        formObject={{
            data: {heading: item.heading},
            children: item.alternatives.map((a) => a.id),
        }}
        objects={Object.fromEntries(
            item.alternatives.map((a) => [a.id, {data: {heading: a.heading}}])
        )}
        onChange={item.select}
        onFocus={(e?: FocusEvent) => {
            onPromptNewFilling(e);
            item.focus.onFocus(e);
        }}
        onBlur={(e?: FocusEvent) => item.focus.onBlur(e)}
    />
);

const ListField = ({item, isDraft, onPromptNewFilling}: {item: ListItem} & DraftProps) => (
    <XListForm
        ref={item.focus.ref}
        header={item.heading}
        rows={item.rows}
        maxRows={item.maxRows}
        onChange={(e: {target: {value: string}}) =>
            item.setRows(Number(e.target.value))
        }
        onFocus={(e?: FocusEvent) => {
            onPromptNewFilling(e);
            item.focus.onFocus(e);
        }}
        onBlur={(e?: FocusEvent) => item.focus.onBlur(e)}
        onCommitRows={() => item.commitRows()}
    >
        {item.rowItems.map((row, index) => (
            <div key={`${item.id}-form-${index + 1}`}>
                {row.map((variable) => (
                    <VariableField
                        key={variable.inputId}
                        item={variable}
                        isDraft={isDraft}
                        onPromptNewFilling={onPromptNewFilling}
                    />
                ))}
            </div>
        ))}
    </XListForm>
);

const ConditionField = ({item}: {item: ConditionItem}) => (
    <ConditionalsForm
        ref={item.focus.ref}
        id={item.id}
        name={item.name}
        displayValue={item.selectedLabelId}
        children={item.labels.map((l) => ({label_id: l.id, label_name: l.name}))}
        onChange={item.select}
        onFocus={(e) => item.focus.onFocus(e)}
        onBlur={(e) => item.focus.onBlur(e)}
    />
);

/** The document generator form: condition radios, then the form items. */
export const DocgenForm = ({isDraft, onPromptNewFilling}: DraftProps) => {
    const {conditions, items} = useFormItems();
    return (
        <div>
            {conditions.length > 0 && (
                <div>
                    {conditions.map((item) => (
                        <ConditionField key={item.id} item={item} />
                    ))}
                </div>
            )}
            <div>
                {items.map((item, index) => (
                    <div
                        key={item.kind === "variable" ? item.inputId : item.id}
                        data-counter={index}
                        data-description="form-object"
                    >
                        {item.kind === "variable" && (
                            <VariableField
                                item={item}
                                isDraft={isDraft}
                                onPromptNewFilling={onPromptNewFilling}
                            />
                        )}
                        {item.kind === "optional" && (
                            <OptionalField
                                item={item}
                                isDraft={isDraft}
                                onPromptNewFilling={onPromptNewFilling}
                            />
                        )}
                        {item.kind === "alternatives" && (
                            <AlternativesField
                                item={item}
                                isDraft={isDraft}
                                onPromptNewFilling={onPromptNewFilling}
                            />
                        )}
                        {item.kind === "list" && (
                            <ListField
                                item={item}
                                isDraft={isDraft}
                                onPromptNewFilling={onPromptNewFilling}
                            />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
